/**
 * Date utility functions to handle timezone issues and ensure consistent date parsing
 */

/**
 * Parse a date string as UTC to avoid timezone conversion issues
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Date object in UTC
 */
export const parseUTCDate = (dateString: string): Date => {
  // Ensure we parse as UTC by appending timezone info
  const utcDateString = dateString.includes('T') ? dateString : `${dateString}T00:00:00.000Z`;
  return new Date(utcDateString);
};

/**
 * Get today's date as a UTC date (without time component)
 * @returns Date object representing today in UTC
 */
export const getTodayUTC = (): Date => {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), today.getDate());
};

/**
 * Compare two dates ignoring time components
 * @param date1 - First date
 * @param date2 - Second date
 * @returns true if date1 is before date2 (ignoring time)
 */
export const isDateBefore = (date1: Date, date2: Date): boolean => {
  const d1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
  const d2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());
  return d1.getTime() < d2.getTime();
};

/**
 * Check if a date string represents a future date (or today)
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns true if the date is today or in the future
 */
export const isFutureOrToday = (dateString: string): boolean => {
  const date = parseUTCDate(dateString);
  const today = getTodayUTC();
  return !isDateBefore(date, today);
};

/**
 * Create a UTC date for consistent month comparisons with date-fns
 * This ensures all dates are in the same timezone context for isSameMonth comparisons
 * @param year - Year
 * @param month - Month (0-11)
 * @param day - Day (1-31)
 * @returns Date object in local timezone but representing the UTC date
 */
export const createConsistentDate = (year: number, month: number, day: number): Date => {
  return new Date(year, month, day);
};

/**
 * Parse a date string consistently for calendar operations
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Date object for use with date-fns functions
 */
export const parseConsistentDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return createConsistentDate(year, month - 1, day); // month - 1 because Date uses 0-based months
};

/**
 * Debug logging helper for date-related operations
 * @param context - Context description
 * @param dateString - Date string being processed
 * @param additionalInfo - Additional debug information
 */
export const debugDateOperation = (context: string, dateString: string, additionalInfo?: any): void => {
  if (process.env.NODE_ENV === 'development') {
    const date = parseUTCDate(dateString);
    const consistentDate = parseConsistentDate(dateString);
    const today = getTodayUTC();
    console.log(`[Date Debug] ${context}:`, {
      dateString,
      parsedUTCDate: date.toISOString(),
      parsedConsistentDate: consistentDate.toString(),
      today: today.toISOString(),
      isFutureOrToday: isFutureOrToday(dateString),
      ...additionalInfo
    });
  }
};